import React from "react";
import { Button, Stack, Typography, Box, Tooltip } from "@mui/material";

const ScheduleModalActions = ({
  selectedDay,
  selectedTime,
  formatSelectedDateTime,
  confirmSchedule,
  isSaveDisabled,
  isLoading,
  isPageLoading = false,
  saveButtonText,
  theme,
  isCardVerified,
  openVerifyCardModal,
  meetingData,
  // New props for reschedule mode
  isRescheduleMode = false,
}) => {
  const isSessionFree = meetingData?.session?.session_rate === 0;

  const isButtonDisabled =
    isPageLoading ||
    (isSaveDisabled && (isRescheduleMode || isCardVerified)) ||
    isLoading;

  const tooltipMessage =
    !isCardVerified && !isSessionFree && !isRescheduleMode
      ? "An active credit card is required to schedule paid meetings."
      : !selectedTime
        ? isRescheduleMode
          ? "Please select a new time to reschedule the meeting."
          : "Please select a time to schedule a meeting."
        : "";

  // Update button click handler
  const handleButtonClick = () => {
    if (isRescheduleMode || isSessionFree || isCardVerified) {
      confirmSchedule();
    } else {
      openVerifyCardModal();
    }
  };

  // Update button text logic
  const getButtonText = () => {
    if (isPageLoading) return "Updating";
    if (isLoading) {
      return isRescheduleMode ? "Rescheduling..." : "Sending...";
    }
    if (!isCardVerified && !isSessionFree && !isRescheduleMode) {
      return "Add Payment Method";
    }
    return saveButtonText;
  };

  // Helper text for different modes
  const getHelperText = () => {
    if (isRescheduleMode) {
      if (!selectedTime) {
        return "Please select a new time slot for your meeting.";
      }
      return "Reschedule now! This time slot may be claimed by another user soon.";
    }

    if (!isCardVerified && !isSessionFree) {
      return "An active credit card is required to schedule paid meetings.";
    }

    if (isPageLoading) {
      return "Loading schedule options...";
    }

    if (!selectedTime) {
      return "Please select an available time slot to schedule your meeting.";
    }

    return isSessionFree
      ? "Schedule now! This free session time slot may be claimed by another user soon."
      : "Schedule now! This time slot may be claimed by another user soon.";
  };

  const getMobileHelperText = () => {
    if (isPageLoading) {
      return "Loading schedule options...";
    }
    if (!isCardVerified && !isSessionFree && !isRescheduleMode) {
      return "Payment card required for booking";
    }
    if (isRescheduleMode) {
      return "Select a new time slot";
    }
    return "Select a time slot";
  };

  return (
    <>
      {/* Mobile Layout */}
      <Box sx={{ display: { xs: "flex", sm: "none" }, width: "100%" }}>
        <Stack
          direction="column"
          spacing={1}
          sx={{
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
          }}
        >
          {selectedDay && selectedTime ? (
            <Typography align="center" sx={{ width: "100%", mb: 1 }}>
              {formatSelectedDateTime()}
            </Typography>
          ) : (
            <Typography
              align="center"
              variant="body2"
              sx={{
                width: "100%",
                mb: 1,
                color: theme.palette.text.secondary,
              }}
            >
              {getMobileHelperText()}
            </Typography>
          )}

          <Box sx={{ width: "100%" }}>
            <Button
              fullWidth
              variant="contained"
              color={isRescheduleMode ? "warning" : "primary"}
              onClick={handleButtonClick}
              disabled={isButtonDisabled}
              sx={{
                borderRadius: "50px",
                width: "100%",
                fontWeight: 600,
                minHeight: "48px",
                backgroundColor:
                  !isRescheduleMode && theme.palette.mode === "light"
                    ? theme.palette.background.accent
                    : undefined,
                color:
                  !isRescheduleMode && theme.palette.mode === "light"
                    ? theme.palette.common.white
                    : undefined,
                "&:hover": {
                  backgroundColor:
                    !isRescheduleMode && theme.palette.mode === "light"
                      ? theme.palette.background.accentHover
                      : undefined,
                  color:
                    !isRescheduleMode && theme.palette.mode === "light"
                      ? theme.palette.common.white
                      : undefined,
                },
              }}
            >
              {getButtonText()}
            </Button>
          </Box>
        </Stack>
      </Box>
      {/* Desktop Layout */}
      <Box sx={{ display: { xs: "none", sm: "flex" }, width: "100%", mt: 2 }}>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: "center",
            justifyContent: "flex-end",
            width: "100%",
          }}
        >
          <Box sx={{ flexGrow: 1 }} />

          {selectedDay && selectedTime && (
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, color: theme.palette.text.primary }}
            >
              {formatSelectedDateTime()}
            </Typography>
          )}

          <Tooltip title={tooltipMessage} placement="top">
            <Box>
              <Button
                variant="contained"
                color={isRescheduleMode ? "warning" : "primary"}
                onClick={handleButtonClick}
                disabled={isButtonDisabled}
                sx={{
                  borderRadius: "50px",
                  width: "250px",
                  fontWeight: 600,
                  minHeight: "48px",
                  backgroundColor:
                    !isRescheduleMode && theme.palette.mode === "light"
                      ? theme.palette.background.accent
                      : undefined,
                  color:
                    !isRescheduleMode && theme.palette.mode === "light"
                      ? theme.palette.common.white
                      : undefined,
                  "&:hover": {
                    backgroundColor:
                      !isRescheduleMode && theme.palette.mode === "light"
                        ? theme.palette.background.accentHover
                        : undefined,
                    color:
                      !isRescheduleMode && theme.palette.mode === "light"
                        ? theme.palette.common.white
                        : undefined,
                  },
                }}
              >
                {getButtonText()}
              </Button>
            </Box>
          </Tooltip>
        </Stack>
      </Box>
      {/* Helper text - Desktop only */}
      <Box sx={{ mt: 2, mb: 2, display: { xs: "none", sm: "flex" } }}>
        <Typography
          variant="body2"
          color={theme.palette.text.secondary}
          sx={{
            fontWeight: !selectedTime ? 400 : 500,
            fontSize: "14px",
          }}
        >
          {getHelperText()}
        </Typography>
      </Box>
    </>
  );
};

export default ScheduleModalActions;
